<template>
	<header aria-label="page-hero" class="hero landing-page-hero pb-0 position-relative">
		<div :class="['py-150 py-200-md', { container: !appStore.isMobile }]">
			<div class="display-flex flex-direction-column-smo gap-150-smo">
				<div class="col-6-md">
					<div :class="{ container: appStore.isMobile }">
						<h1 class="heading-xl" v-html="heading" />
						<p class="body-lg-md text-color-content-secondary" v-html="heroDescription" />
						<div class="mt-150 mt-200-md">
							<p class="body-sm text-color-content-tertiary"
								>Start investing in less than 5 minutes and with as little as&nbsp;$10.</p
							>
							<LeadCaptureComponent class="mt-100" :form-config="leadCaptureConfig" />
						</div>
					</div>
					<SocialProofSection v-if="!appStore.isMobile" />
				</div>
				<div class="col-6-md py-100-smo pt-200-md pl-200-md pl-500-lg image-container">
					<img
						:src="imageSrc"
						alt="Diversified real estate and venture assets"
						role="presentation"
						fetchpriority="high"
					/>
				</div>
			</div>
			<SocialProofSection v-if="appStore.isMobile" class="px-100" />
			<div class="body-xs mt-100 px-100-smo text-align-center-smo text-color-content-tertiary">
				The person who invited you has agreed to the full 
				<BaseLink classes="text-color-current-color text-decoration-underline" :link="termsAndConditionsLink" />
				of the Fundrise Invitation Program, and by entering your email above you agree to the same terms and
				conditions.
			</div>
		</div>
	</header>
</template>

<script setup lang="ts">
import { computed, reactive } from 'vue';
import { currency, deorphanText } from '@filters/shared-filters';
import { appCloudfrontPath } from '@constants';
import LeadCaptureComponent from '@components/lead-capture/lead-capture-form.vue';
import { LeadCaptureFormConfig } from 'types/layout/lead';
import { Link } from 'types/layout';
import { ReferrerUser } from 'types/referral';
import SocialProofSection from '@components/marketing-site/social-proof-section.vue';
import { useAppStore } from '@stores/app';

interface Props {
	referrerUser: ReferrerUser | null;
	leadCaptureReferralKey: string;
}

const props = defineProps<Props>();

const appStore = useAppStore();

const imageSrc = `${appCloudfrontPath}/images/invite-center/invite-center-hero.webp`;

const termsAndConditionsLink: Link = {
	router: 'referral-terms-conditions',
	text: 'terms and conditions',
	target: '_blank'
};

const leadCaptureConfig: LeadCaptureFormConfig = reactive({
	buttonText: 'Sign up',
	signupFlowType: 'STANDARD',
	promoInvestmentType: 'INVITATION_PROGRAM_INVITEE',
	referralKey: props.leadCaptureReferralKey,
	thirdPartyAuthDisclaimerClass: 'text-color-content-tertiary'
});

const rewardPerInviteDisplay = computed((): string => {
	return currency(props.referrerUser?.amount ?? '50', 0);
});

const heading = computed((): string => {
	return deorphanText(`Sign up now to get ${rewardPerInviteDisplay.value} in shares`);
});

const heroDescription = computed((): string => {
	const referrerIntroText = props.referrerUser?.referrerName
		? `${props.referrerUser?.referrerName} invited you`
		: `You've been invited`;
	return deorphanText(
		`${referrerIntroText} to join the 2+ million people using Fundrise to start investing in private assets.`
	);
});
</script>

<style lang="scss" scoped>
@use '../../styles/utilities/respond-to.scss' as *;

.image-container {
	@include respond-to(smo) {
		background-color: white;
	}
}

img {
	margin: auto;
	@include respond-to(smo) {
		width: 75%;
	}
}
</style>
